import * as Yup from 'yup';
import AllForm from '../../stateless/Form';
import {
  Box,
  Button,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { UseCreateChoice, useEditChoice } from '../../Query/ChoiceQuery';

import { useAtom } from 'jotai';

import { useState, useEffect, useMemo } from 'react';
import { useAllStores } from '../../Query/ManageStoreQuery';

import { FieldProps, FormikProps } from 'formik';
import { choiceAtom } from './ManageChoiceTable';
import { useThemeColors } from '../../../Hooks/useThemeColors';
import { COLORS } from '../../../theme/themeConstants';
import { useTranslation } from 'react-i18next';
import { useStoreManagementParams } from './useStoreManagementParams';


const AddChoice = ({ onCancel }: { onCancel: () => void }) => {
  const { t, i18n } = useTranslation();
  const [, forceUpdate] = useState({});
  const [choiceDataId] = useAtom(choiceAtom);
  // const [selectedStoreId, setSelectedStoreId] = useState(choiceDataId ? choiceDataId.storeId : '');
  const { warning } = useThemeColors();
  const [storeType, setStoreType] = useState('');

  // Force re-render when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      forceUpdate({});
    };

    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const { mutate: CreateChoice, isPending: CreateChoiceLoading } =
    UseCreateChoice();

  const { mutate: EditChoice, isPending: EditChoiceLoading } = useEditChoice();


  const [storeOptions, setStoreOptions] = useState<{ label: string; value: string; storeType: string }[]>([]);

  const { params } = useStoreManagementParams();
  const isReady = Boolean(params && Object.keys(params).length > 0);
  const memoizedParams = useMemo(() => {
    return isReady ? params : {};
  }, [isReady, params]);

  const {
    data: storesData, isLoading: isStoresLoading
  } = useAllStores(memoizedParams);


  useEffect(() => {
    if (storesData && Array.isArray(storesData.data)) {
      const options = storesData.data.map((store: any) => ({
        value: store.id,
        label: store.restaurantDetails.storeName,
        storeType: store.restaurantDetails.storeType,
      }));
      setStoreOptions(options);
    } else {
      setStoreOptions([]);
    }
  }, [storesData]);

  // Clear storeId when no stores are available
  useEffect(() => {
    if (storeOptions.length === 0 && !isStoresLoading) {
      // This will be handled by the form component's enableReinitialize
    }
  }, [storeOptions.length, isStoresLoading]);


  // Validation schema depends on add/edit mode
  const validationSchema = useMemo(() =>
    Yup.object().shape({
      choiceNameEnglish: Yup.string()
        .required(t('choice.form.validation.requiredChoiceNameEnglish'))
        .matches(/^[A-Za-z][A-Za-z\s]*$/, 'Choice Name should only contain letters and spaces, and not start with a space')
        .max(100, 'Choice Name cannot exceed 100 characters') // corrected max from 50 to 100
        .test(
          'not-only-spaces',
          'Choice Name cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      choiceNameArabic: Yup.string()
        .required(t('choice.form.validation.requiredChoiceNameArabic'))
        .matches(
          /^[\u0600-\u06FF][\u0600-\u06FF\s]*$/,
          'Name should only contain Arabic letters and spaces, and not start with a space'
        )
        .max(50, 'Choice Name cannot exceed 50 characters')
        .test(
          'not-only-spaces',
          'Name cannot be only spaces',
          (value) => typeof value === 'string' && value.trim().length > 0
        ),
      price: Yup.number()
        .typeError('Price must be a number')
        .required(t('choice.form.validation.requiredPrice'))
        .positive('Price must be greater than 0'),
      // merchantId: choiceDataId ? Yup.string() : Yup.string().required('Please select Merchant'),
      storeId: Yup.string()       
        .required(t('choice.form.validation.requiredStore'))
    }),
    [choiceDataId, t]);

  return (
    <>
      {AllForm({
        initialValues: {
          choiceNameEnglish: choiceDataId
            ? choiceDataId?.choiceNameEnglish
            : '',
          choiceNameArabic: choiceDataId ? choiceDataId?.choiceNameArabic : '',
          // merchantId: choiceDataId ? choiceDataId?.merchantId : '',
          price: choiceDataId ? choiceDataId?.price : null,
          storeId: choiceDataId ? choiceDataId?.storeId : '',
        },
        validationSchema,
        validateOnChange: true,
        enableReinitialize: true,
        onSubmit: async (values: any) => {
          // Check if stores are available for the selected merchant
          const noStoresAvailable = values.merchantId && storeOptions.length === 0 && !isStoresLoading;

          if (!choiceDataId && noStoresAvailable) {
            return;
          }

          // For edit, use values from choiceDataId; for add, use form values
          const modifiedValues = choiceDataId
            ? {
              storeType: choiceDataId?.storeType,
              storeId: choiceDataId.storeId,
              merchantId: choiceDataId.merchantId,
              choiceNameEnglish: values.choiceNameEnglish,
              choiceNameArabic: values.choiceNameArabic,
              price: +values.price,
              addedBy: 'Merchant',
            }
            : {
              storeType: storeType,
              storeId: values.storeId,
              merchantId: values.merchantId,
              choiceNameEnglish: values.choiceNameEnglish,
              choiceNameArabic: values.choiceNameArabic,
              price: +values.price,
              addedBy: 'Merchant',
            };

          try {
            if (choiceDataId) {
              EditChoice(
                {
                  id: choiceDataId.id || '',
                  data: modifiedValues,
                },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.error('Error updating choice:', error);
                    // Handle error appropriately, e.g., show a message to the user
                    // setErrors(parseJavaError(error)); // Uncomment if you have a function to parse errors
                  },
                }
              );
            } else {
              CreateChoice(
                { modifiedValues, storeId: values.storeId },
                {
                  onSuccess: () => {
                    onCancel();
                  },
                  onError: (error: any) => {
                    console.error('Error creating choice:', error);
                    // Handle error appropriately, e.g., show a message to the user
                  },
                }
              );
            }
          } catch (error) {
            console.error('Error submitting form:', error);
            // Handle error appropriately, e.g., show a message to the user
            // setErrors(parseJavaError(error)); // Uncomment if you have a function to parse errors
          }
        },

        fields: [
          // Only show these fields if not in edit mode
          ...(!choiceDataId ? [

            {
              name: 'storeId',
              label: t('choice.form.selectStore'),
              type: 'autocomplete',
              isRequired: true,
              component: ({ form }: { field: FieldProps['field']; form: FormikProps<any> }) => {
                return (
                  <Box sx={{ width: 400 }}>
                    <Autocomplete
                      options={storeOptions}
                      getOptionLabel={(option) => option.label}
                      value={storeOptions.find(opt => opt.value === form.values.storeId) || null}
                      onChange={(_, newValue) => {
                        // Update form value and storeType when a new store is selected
                        form.setFieldValue('storeId', newValue ? newValue.value : '');
                        setStoreType(newValue ? newValue?.storeType : '')
                      }}
                      fullWidth
                      sx={{
                        width: '100%', // Ensure Autocomplete itself expands
                        '& .MuiInputBase-root': {
                          width: '100%', // Force input root width
                        },
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('choice.form.placeholders.selectStore')}
                          variant="outlined"
                          fullWidth
                        />
                      )}
                    />
                  </Box>
                );
              },
            },
          ] : []),
          // Always show these fields
          {
            name: 'choiceNameEnglish',
            label: t('choice.form.choiceNameEnglish'),
            type: 'text',
            isRequired: true,
          },
          {
            name: 'choiceNameArabic',
            label: t('choice.form.choiceNameArabic'),
            type: 'text',
            isRequired: true,
          },
          {
            name: 'price',
            label: t('choice.form.price'),
            type: 'number',
            isRequired: true,
          },
        ],

        customButton: (formikProps: any) => (
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              type="submit"
              variant="contained"
              sx={{
                textTransform: 'none',
                fontSize: 14,
                px: 3,
                py: 1,
                backgroundColor: COLORS.primary.main,
                '&:hover': {
                  backgroundColor: COLORS.primary.dark,
                },
                '&:disabled': {
                  backgroundColor: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)',
                },
              }}
              onClick={(e) => {
                e.preventDefault();
                formikProps.handleSubmit();
              }}
              disabled={CreateChoiceLoading || EditChoiceLoading}
            >
              {CreateChoiceLoading || EditChoiceLoading ? (
                <>
                  <CircularProgress size={16} sx={{ color: '#fff', mr: 1 }} />
                  {choiceDataId ? t('choice.form.updating') : t('choice.form.creating')}
                </>
              ) : choiceDataId ? (
                t('choice.form.update')
              ) : (
                t('choice.form.create')
              )}
            </Button>

            {!choiceDataId && (
              <Button
                type="button"
                variant="outlined"
                onClick={() => {
                  formikProps.resetForm();
                  formikProps.setFieldValue('storeId', '');
                  setStoreType(''); // Reset storeType as well
                }}
                sx={{
                  textTransform: 'none',
                  fontSize: 14,
                  px: 3,
                  py: 1,
                  borderColor: COLORS.secondary.main,
                  color: COLORS.secondary.main,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    color: 'rgba(0, 0, 0, 0.26)',
                  },
                }}
              >
                {t('choice.form.reset')}
              </Button>
            )}
            <Button
              type="button"
              variant="outlined"
              color="error"
              onClick={onCancel}
              sx={{
                bgcolor: warning.main,
                color: '#fff',
                '&:hover': {
                  bgcolor: warning.dark,
                  color: '#fff',
                },
              }}
            >
              {t('choice.form.cancel')}
            </Button>
          </Box>
        ),
      })()}
    </>
  );
};

export default AddChoice;
